/* eslint-disable */
import React from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import './styles/styles.css';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'Bins Storage – San Francisco Bay Area',
  description:
    'On-demand valet storage in San Francisco. We pick up, store, and deliver your belongings – no truck, no hassle. Serving SF, Oakland, Berkeley & the Bay Area.',
  url: 'https://www.bins-storage.com/san-francisco',
  areaServed: [
    { '@type': 'City', name: 'San Francisco' },
    { '@type': 'City', name: 'Oakland' },
    { '@type': 'City', name: 'Berkeley' },
    { '@type': 'City', name: 'Daly City' },
    { '@type': 'City', name: 'San Mateo' },
    { '@type': 'AdministrativeArea', name: 'San Francisco Bay Area' },
  ],
  priceRange: '$$',
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.bins-storage.com/' },
    { '@type': 'ListItem', position: 2, name: 'San Francisco', item: 'https://www.bins-storage.com/san-francisco' },
  ],
};

const sfFaqs = [
  {
    q: 'Do you pick up from SF apartments without elevators?',
    a: 'Yes. Most SF buildings don\'t have elevators – our team is trained for walk-ups and tight staircases. There may be an additional fee for 3+ floor walk-ups; you\'ll see it upfront when you reserve.',
  },
  {
    q: 'How fast can you pick up in San Francisco?',
    a: 'Same-day and next-day slots are available in most SF neighborhoods. Longer lead times may apply during peak moving seasons (May–August).',
  },
  {
    q: 'Where is my stuff actually stored?',
    a: 'At one of our partner storage facilities in the Bay Area. You choose which facility during signup, and every facility is climate-controlled, insured, and 24/7 monitored.',
  },
  {
    q: 'Is Bins cheaper than renting a self-storage unit in SF?',
    a: 'For most SF renters, yes – especially once you factor in the cost of renting a truck, gas, tolls, and your time. Compare apples-to-apples on our reserve page.',
  },
  {
    q: 'Can I access my storage unit directly?',
    a: 'Generally no – our team handles all pickups and deliveries so your items stay secure. In certain circumstances we can accommodate direct access. Contact us and we\'ll work with you.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: sfFaqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

const combinedSchema = [localBusinessSchema, breadcrumbSchema, faqSchema];

// ─── Styles ───────────────────────────────────────────────────────────────────

const heroStyle = {
  backgroundColor: '#7B1FA2',
  padding: '4rem 2rem 3rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
};

const heroHeadingStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: 'clamp(2rem, 5vw, 3.5rem)',
  fontWeight: 700,
  color: '#FFF',
  margin: '0 0 1rem',
};

const heroSubStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.2rem',
  color: 'rgba(255,255,255,0.88)',
  maxWidth: 620,
  lineHeight: 1.6,
  margin: '0 0 0.5rem',
};

const ctaRow = {
  display: 'flex',
  gap: '1rem',
  flexWrap: 'wrap',
  justifyContent: 'center',
  margin: '1.5rem 0 0.75rem',
};

const ctaPrimary = {
  display: 'inline-block',
  backgroundColor: '#FFF',
  color: '#7B1FA2',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1.1rem',
  padding: '14px 32px',
  borderRadius: 999,
  textDecoration: 'none',
};

const ctaSecondary = {
  display: 'inline-block',
  backgroundColor: 'transparent',
  color: '#FFF',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1.1rem',
  padding: '14px 32px',
  borderRadius: 999,
  textDecoration: 'none',
  border: '2px solid rgba(255,255,255,0.6)',
};

const trustLine = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.9rem',
  color: 'rgba(255,255,255,0.75)',
  margin: '0.5rem 0 0',
};

const sectionStyle = {
  padding: '3.5rem 2rem',
  maxWidth: 900,
  margin: '0 auto',
};

const sectionHeadingStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '2rem',
  fontWeight: 700,
  color: '#261136',
  marginBottom: '0.5rem',
  textAlign: 'center',
};

const sectionSubStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.05rem',
  color: '#555',
  textAlign: 'center',
  maxWidth: 640,
  margin: '0 auto 2.5rem',
  lineHeight: 1.65,
};

const cardsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '1.5rem',
};

const card = {
  backgroundColor: '#F9F6FC',
  borderRadius: 12,
  padding: '1.75rem 1.5rem',
  textAlign: 'center',
};

const cardTitle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.1rem',
  fontWeight: 700,
  color: '#261136',
  marginBottom: '0.5rem',
};

const cardBody = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.95rem',
  color: '#555',
  lineHeight: 1.6,
};

const stepNumber = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '2.5rem',
  fontWeight: 800,
  color: '#7B1FA2',
  marginBottom: '0.5rem',
};

const areaSection = {
  backgroundColor: '#F9F6FC',
  padding: '3rem 2rem',
  textAlign: 'center',
};

const areaGrid = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.75rem',
  justifyContent: 'center',
  maxWidth: 700,
  margin: '1.5rem auto 0',
};

const areaBadge = {
  backgroundColor: '#EDE0F7',
  color: '#5B1F8A',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 600,
  fontSize: '0.9rem',
  padding: '8px 18px',
  borderRadius: 999,
};

const pricingSection = {
  backgroundColor: '#261136',
  padding: '3.5rem 2rem',
  textAlign: 'center',
};

const pricingHeadingStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '2rem',
  fontWeight: 700,
  color: '#FFF',
  marginBottom: '0.5rem',
};

const pricingSubStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.05rem',
  color: '#AAB5E0',
  marginBottom: '2rem',
};

const pricingCtaStyle = {
  display: 'inline-block',
  backgroundColor: '#7B1FA2',
  color: '#FFF',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1rem',
  padding: '12px 28px',
  borderRadius: 999,
  textDecoration: 'none',
  border: '2px solid rgba(255,255,255,0.3)',
};

const faqSectionStyle = {
  padding: '3.5rem 2rem',
  maxWidth: 720,
  margin: '0 auto',
};

const faqItem = {
  borderBottom: '1px solid #EEE',
  paddingBottom: '1.25rem',
  marginBottom: '1.25rem',
};

const faqQuestion = {
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1.05rem',
  color: '#261136',
  marginBottom: '0.4rem',
};

const faqAnswer = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.97rem',
  color: '#555',
  lineHeight: 1.65,
};

const finalCtaSection = {
  backgroundColor: '#7B1FA2',
  padding: '4rem 2rem',
  textAlign: 'center',
};

const finalCtaHeading = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: 'clamp(1.5rem, 4vw, 2.25rem)',
  fontWeight: 700,
  color: '#FFF',
  margin: '0 0 0.75rem',
};

const finalCtaSub = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.1rem',
  color: 'rgba(255,255,255,0.85)',
  margin: '0 0 2rem',
  textAlign: 'center',
};

const internalLinksSection = {
  backgroundColor: '#F9F6FC',
  padding: '2rem',
  textAlign: 'center',
};

const internalLinkStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.95rem',
  color: '#7B1FA2',
  margin: '0 0.75rem',
  textDecoration: 'underline',
};

// ─── Component ────────────────────────────────────────────────────────────────

const SanFrancisco = () => (
  <>
    <SEO
      title="Valet Storage in San Francisco | Pickup & Delivery | Bins Storage"
      description="On-demand valet storage in San Francisco. We pick up, store, and deliver your stuff – no truck, no hassle. Serving SF, Oakland, Berkeley & the Bay Area. Reserve in minutes."
      path="/san-francisco"
      schema={combinedSchema}
    />

    {/* HERO */}
    <section style={heroStyle}>
      <h1 style={heroHeadingStyle}>Valet Storage in San Francisco</h1>
      <p style={heroSubStyle}>
        We pick it up, we store it, we bring it back. No truck rental, no heavy
        lifting, no wasted weekends.
      </p>
      <div style={ctaRow}>
        <a href="/Download" style={ctaPrimary} data-cta="sf-hero-reserve">
          Reserve Now
        </a>
        <a href="#how-it-works" style={ctaSecondary} data-cta="sf-hero-hiw">
          See How It Works
        </a>
      </div>
      <p style={trustLine}>
        Serving San Francisco, Oakland, Berkeley, Daly City, South Bay &amp; the East Bay.
      </p>
    </section>

    {/* BENEFITS */}
    <section style={{ backgroundColor: '#FFF', padding: '3.5rem 2rem' }}>
      <div style={sectionStyle}>
        <h2 style={sectionHeadingStyle}>Built for San Francisco Living</h2>
        <p style={sectionSubStyle}>
          Storage that works around SF's tight streets, tiny apartments, and
          impossible parking.
        </p>
        <div style={cardsGrid}>
          <div style={card}>
            <div style={cardTitle}>🚗 No truck needed</div>
            <div style={cardBody}>
              SF streets are tight, parking is impossible, and most apartments
              don't have a loading zone. Our team shows up with the right vehicle
              and the right gear.
            </div>
          </div>
          <div style={card}>
            <div style={cardTitle}>📦 Studio-sized or storage-unit-sized</div>
            <div style={cardBody}>
              Store one box or a whole apartment. Pay for what you use. Compare
              sizes and pricing across Bay Area facilities in one place.
            </div>
          </div>
          <div style={card}>
            <div style={cardTitle}>🗓 Pickup &amp; delivery on your schedule</div>
            <div style={cardBody}>
              Same-day and next-day slots in most SF neighborhoods. Book in the
              app, track your team, done.
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* HOW IT WORKS */}
    <section id="how-it-works" style={{ backgroundColor: '#F9F6FC', padding: '3.5rem 2rem' }}>
      <div style={sectionStyle}>
        <h2 style={sectionHeadingStyle}>How Valet Storage Works in SF</h2>
        <div style={cardsGrid}>
          {[
            { n: '1', t: 'Reserve online', b: 'Pick your facility, unit size, and a pickup window on bins-storage.com or in the app.' },
            { n: '2', t: 'We pick up', b: 'Our team arrives at your SF apartment, carefully loads everything, and takes it to a secure facility in the Bay Area.' },
            { n: '3', t: 'We store it', b: 'Every facility in our network is climate-controlled, monitored 24/7, and insured.' },
            { n: '4', t: 'We bring it back', b: 'Need something? Schedule a delivery in the app. We\'ll bring individual items or the whole unit back to your door.' },
          ].map(({ n, t, b }) => (
            <div key={n} style={card}>
              <div style={stepNumber}>{n}</div>
              <div style={cardTitle}>{t}</div>
              <div style={cardBody}>{b}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* NEIGHBORHOODS */}
    <section style={areaSection}>
      <h2 style={{ ...sectionHeadingStyle, marginBottom: '0.5rem' }}>
        Neighborhoods We Serve
      </h2>
      <div style={areaGrid}>
        {[
          'SoMa', 'Mission', 'Hayes Valley', 'Nob Hill', 'Russian Hill',
          'Marina', 'Pacific Heights', 'Richmond', 'Sunset',
          'Oakland', 'Berkeley', 'Emeryville', 'Albany',
          'Daly City', 'South San Francisco', 'San Mateo',
          'Palo Alto', 'Mountain View', 'San Jose (on request)',
        ].map((area) => (
          <span key={area} style={areaBadge}>{area}</span>
        ))}
      </div>
      <p style={{ fontFamily: 'Raleway, sans-serif', fontSize: '0.95rem', color: '#666', marginTop: '1.5rem' }}>
        Don't see your neighborhood?{' '}
        <a href="/Contact" style={{ color: '#7B1FA2' }}>Contact us</a> – we likely cover it.
      </p>
    </section>

    {/* WHO WE SERVE */}
    <section style={{ backgroundColor: '#FFF', padding: '3.5rem 2rem' }}>
      <div style={sectionStyle}>
        <h2 style={sectionHeadingStyle}>Who We Serve</h2>
        <div style={cardsGrid}>
          <div style={card}>
            <div style={cardTitle}>🎓 Students</div>
            <div style={cardBody}>
              Summer storage while you're home or abroad.{' '}
              <a href="/students" style={{ color: '#7B1FA2' }}>Learn more →</a>
            </div>
          </div>
          <div style={card}>
            <div style={cardTitle}>🏠 Apartment Renters</div>
            <div style={cardBody}>
              Free up space in your tiny SF apartment.{' '}
              <a href="/apartment-storage" style={{ color: '#7B1FA2' }}>Learn more →</a>
            </div>
          </div>
          <div style={card}>
            <div style={cardTitle}>📦 Movers Between Apartments</div>
            <div style={cardBody}>
              Bridge the gap between leases without paying for two places.
            </div>
          </div>
          <div style={card}>
            <div style={cardTitle}>✈️ Remote Workers &amp; Travelers</div>
            <div style={cardBody}>
              Leaving SF for a month? Six months? We'll hold your stuff while
              you're gone.
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* PRICING */}
    <section style={pricingSection}>
      <h2 style={pricingHeadingStyle}>San Francisco Storage Pricing</h2>
      <p style={pricingSubStyle}>
        Pricing depends on the facility you choose and the unit size you need.
        Our marketplace lets you compare pricing across Bay Area facilities in
        one place, so you always get a fair rate.
      </p>
      <a href="/Download" style={pricingCtaStyle} data-cta="sf-pricing-reserve">
        Compare SF Pricing →
      </a>
    </section>

    {/* FAQ */}
    <section style={faqSectionStyle}>
      <h2 style={{ ...sectionHeadingStyle, textAlign: 'left', marginBottom: '1.75rem' }}>
        San Francisco FAQ
      </h2>
      {sfFaqs.map(({ q, a }) => (
        <div key={q} style={faqItem}>
          <div style={faqQuestion}>{q}</div>
          <div style={faqAnswer}>
            {a.replace('Contact us', '').replace('our reserve page', '')}{' '}
            {a.includes('reserve page') && (
              <a href="/Download" style={{ color: '#7B1FA2' }}>reserve page</a>
            )}
            {a.includes('Contact us') && (
              <><a href="/Contact" style={{ color: '#7B1FA2' }}>Contact us</a> and we'll work with you.</>
            )}
          </div>
        </div>
      ))}
    </section>

    {/* FINAL CTA */}
    <section style={finalCtaSection}>
      <h2 style={finalCtaHeading}>Ready to free up space in your SF apartment?</h2>
      <p style={finalCtaSub}>Reserve your storage unit in minutes. No truck, no hassle.</p>
      <a href="/Download" style={ctaPrimary} data-cta="sf-final-reserve">
        Reserve Now
      </a>
    </section>

    {/* INTERNAL LINKS */}
    <section style={internalLinksSection}>
      <p style={{ fontFamily: 'Raleway, sans-serif', color: '#666', fontSize: '0.9rem', margin: '0 0 0.75rem', textAlign: 'center' }}>
        Explore more
      </p>
      <a href="/tacoma" style={internalLinkStyle}>Tacoma Storage</a>
      <a href="/students" style={internalLinkStyle}>Student Storage</a>
      <a href="/apartment-storage" style={internalLinkStyle}>Apartment Storage</a>
      <a href="/FAQ" style={internalLinkStyle}>FAQ</a>
      <a href="/About" style={internalLinkStyle}>About</a>
    </section>
  </>
);

export default SanFrancisco;
